import { Directive, AfterViewInit, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter, ElementRef} from '@angular/core';

/**
 * @directive VisibleDirective
 * @description Detects the visibility of an element in the viewport using the IntersectionObserver API.
 * Emits an event when the visibility state changes.
 *
 * This directive is useful for optimizing performance by triggering events or computations only when the element is visible.
 * 
 * @example
 * ```html
 * <div uppVisible (visibilityChange)="onVisibilityChange($event)">
 *   Observed Content
 * </div>
 * ```
 * 
 * ```typescript
 * onVisibilityChange(isVisible: boolean) {
 *   console.log('Element is visible:', isVisible);
 * }
 * ```
 */
@Directive({
    selector: '[uppVisible]'
})
export class VisibleDirective implements AfterViewInit, OnDestroy {
    private observer: IntersectionObserver | null = null;

    /**
     * @input threshold
     * @description Defines the intersection ratio required to trigger the visibility change.
     * Defaults to `0.1` (10% of the element must be visible to trigger the event).
     */    
    @Input() threshold = 0.1;

    /**
     * @output visibilityChange
     * @description Emits a boolean value indicating whether the element is visible in the viewport.
     */    
    @Output() visibilityChange: EventEmitter<boolean> = new EventEmitter();

    /**
     * @constructor
     * @param elementRef Reference to the DOM element associated with the directive.
     */    
    constructor(private elementRef: ElementRef) {
        // nothing to do
    }

    private _isvisible = false;

    /**
     * @property isVisible
     * @description Gets the current visibility state of the element.
     * @returns {boolean} `true` if the element is visible, otherwise `false`.
     */    
    get isVisible(): boolean {
        return this._isvisible;
    }

    /**
     * @private _UpdateVisibility
     * @description Updates the visibility state and emits an event if the state changes.
     * @param {boolean} isCurrentlyVisible - The new visibility state of the element.
     */    
    private _UpdateVisibility(isCurrentlyVisible: boolean) {
        if (this._isvisible !== isCurrentlyVisible) {
            this._isvisible = isCurrentlyVisible;
            this.visibilityChange.emit(this._isvisible);
        }
    }

    /**
     * @method ngAfterViewInit
     * @description Initializes the `IntersectionObserver` and checks the element's initial visibility.
     * If the element is visible upon initialization, it updates the state accordingly.
     */    
    ngAfterViewInit() {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                this._UpdateVisibility(entry.isIntersecting);
            });
        }, { threshold: this.threshold });

        this.observer.observe(this.elementRef.nativeElement);

        setTimeout(() => {
            const _rect = this.elementRef.nativeElement.getBoundingClientRect();
            const _visible = _rect.top < window.innerHeight && _rect.bottom > 0;
            this._UpdateVisibility(_visible);
        }, 0);        
    }

    /**
     * @method ngOnDestroy
     * @description Cleans up and disconnects the `IntersectionObserver` when the directive is destroyed.
     * Prevents memory leaks by ensuring the observer is properly disposed of.
     */    
    ngOnDestroy() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }
}